import { addRowGeoJsonHelper, generateUUID } from 'store/functions/geoJsonFunctions.js';
import { getRowAt, existsInTable } from 'store/functions/inputFunctions.js';
import { getCorrespondingDropdownColumns } from 'store/functions/utils.js';
import { travelCostsAreTyped, vehicleTypesForNewCostRows } from 'store/functions/travelCostTyping.js';
import {checkTripUuids} from "./dataHelpers.js";
import modifyInput from 'functions/general/input_modifiers';
import { 
    COST_VALIDATION_EMPTY_WARNING,
    STATIC_MODEL_SETTINGS,
    STATIC_TABLE_SETTINGS, 
} from 'store/reducers/constants/constants.js';
import { validateRow, validateTable } from './validateRow.js';
import { uniqueValidationHelper, getDefaultValue } from './utils.js';
import {  clearResultsHelper, clearTableDataHelper } from './stateHelpers.js';
import { SAVED_RUNS_PANEL } from 'functions/general/panelNames';


/**
 * Next free "Location N" name.
 *
 * Locations added without a name previously became the literal string
 * 'undefined', which the duplicate-name suffixing then turned into
 * "undefined (1)", "undefined (2)". On geographic models that name is
 * usually replaced by reverse geocoding, but cartesian (x-y) models have no
 * addresses, so it stuck. Numbering from the highest existing "Location N"
 * keeps names stable as rows are added and deleted.
 */
export const nextDefaultLocationName = (state) => {
  const rows = state?.['data']?.['locations'] || [];
  let highest = 0;
  for (const row of rows) {
    const match = /^Location (\d+)$/.exec(String(row?.['locationName'] ?? ''));
    if (match) {
      const n = parseInt(match[1], 10);
      if (n > highest) highest = n;
    }
  }
  return `Location ${highest + 1}`;
};

const applyTableSpecificDefaults = (newRow, state, tableName) => {
  if (tableName === 'locations') {
    if (!newRow['locationName']) {
      newRow['locationName'] = nextDefaultLocationName(state);
    }
    return;
  }
  if (tableName === 'trips') {
    // Derive the location name from its id only when there's an id to resolve
    // and it actually matches a location. A blank trip (the grid's insert-row
    // action dispatches an empty newRow) has no id, so getRowAt returns
    // {exists:false} — reading `.value` off that used to throw and crash the add.
    if (!newRow['fromLocationName'] && newRow['fromLocationId']) {
      const fromRow = getRowAt(state['data'], 'locations', ['UUID'], [newRow['fromLocationId']]);
      if (fromRow.exists) {
        newRow['fromLocationName'] = fromRow.value['locationName'];
      }
    }
    if (!newRow['toLocationName'] && newRow['toLocationId']) {
      const toRow = getRowAt(state['data'], 'locations', ['UUID'], [newRow['toLocationId']]);
      if (toRow.exists) {
        newRow['toLocationName'] = toRow.value['locationName'];
      }
    }
  }
};

/*
  0 is a value, not an absence. This guard used to be a plain falsy test, so any
  column holding 0 was treated as empty and replaced by its default (or null).
  On the globe that only bit the equator and the prime meridian; on a cartesian
  plane the origin is where a depot normally sits, so every x-y scenario lost
  its depot's coordinates and the row failed validation for a latitude it did
  have. Left alone deliberately: `false` follows the old path, since boolean
  defaults are a separate question from this one.
*/
const isMissing = (value) => !value && value !== 0;

const processColumn = (newRow, state, tableName, columnName, validateCorrespondingColumns) => {
  if (isMissing(newRow[columnName])) {
    const defaultValue = getDefaultValue(state, tableName, columnName);
    if (defaultValue || defaultValue === false || defaultValue === 0) {
      newRow[columnName] = defaultValue;
    } else {
      newRow[columnName] = null;
    }
  }

  const dataType = STATIC_TABLE_SETTINGS[tableName]['dataType'][columnName];
  let newValue = newRow[columnName];
  newValue = modifyInput(newValue, dataType, state['data'][tableName], columnName);
  newRow[columnName] = newValue;

  const validationType = STATIC_TABLE_SETTINGS[tableName]['validation'] && STATIC_TABLE_SETTINGS[tableName]['validation'][columnName];
  if (validationType === 'unique') {
    const [valid, adjNewValue] = uniqueValidationHelper({newValue: newRow[columnName], data: state['data'][tableName], row: null, columnName});
    if (!valid) {
      newRow[columnName] = adjNewValue;
    }
  }

  if (isMissing(newRow[columnName])) {
    const defaultValue = getDefaultValue(state, tableName, columnName);
    if (defaultValue) {
      newRow[columnName] = defaultValue;
    }
  }

  if (validateCorrespondingColumns) {
    return getCorrespondingDropdownColumns(tableName, columnName);
  }
  return new Set();
};

const applyColumnDefaultsAndValidation = (newRow, state, tableName, validateCorrespondingColumns) => {
  let correspondingTables = new Set();
  if (!state['tableSettings'][tableName]) return correspondingTables;
  for (const columnName in state['tableSettings'][tableName]['isViewable']) {
    const newCorrespondingTables = processColumn(newRow, state, tableName, columnName, validateCorrespondingColumns);
    correspondingTables = new Set([...correspondingTables, ...newCorrespondingTables]);
  }
  return correspondingTables;
};

const applyDropdownKeys = (newRow, state, tableName) => {
  if (!STATIC_TABLE_SETTINGS[tableName]['dropdownSource']) return;
  for (const col in newRow) {
    const columnDropdownSource = STATIC_TABLE_SETTINGS[tableName]['dropdownSource'][col];
    if (!columnDropdownSource) continue;
    let values = newRow[col];
    if (!values) continue;

    const { keyFieldTarget, keyFieldSource, tableName: sourceTable, column: sourceTableColumn, multi } = columnDropdownSource;
    if (!multi) {
      values = [values];
    }
    values.forEach((val) => {
      const exists = existsInTable(state['data'], sourceTable, [sourceTableColumn], [val]);
      if (exists.exists) {
        if (multi) {
          if (!newRow[keyFieldTarget]) {
            newRow[keyFieldTarget] = [];
          }
          if (!newRow[keyFieldTarget].includes(state['data'][sourceTable][exists.index][keyFieldSource])) {
            newRow[keyFieldTarget].push(state['data'][sourceTable][exists.index][keyFieldSource]);
          }
        } else {
          newRow[keyFieldTarget] = state['data'][sourceTable][exists.index][keyFieldSource];
        }
      }
    });
  }
};

/*
`vehicleTypes` is the answer from vehicleTypesForNewCostRows: an array to write
one row per type, or null to write a single untyped row.

This used to branch on `tableSettings.travel_costs.isViewable.vehicleType` — a
DISPLAY flag — instead of on the matrix the new rows were joining. The published
examples ship a fully typed matrix with that flag false, so adding a stop to one
wrote untyped rows into a typed matrix, which the solver refuses to read: the
run failed with a KeyError. See store/functions/travelCostTyping.js.
*/
const pushCostRowsForVehicleTypes = (newState, costRow, vehicleTypes) => {
  if (vehicleTypes) {
    vehicleTypes.forEach((vehicle_type_row) => {
      // vehicleTypeId as well as the name, as travelCostHelper sets it: rows are
      // matched by type id, and without it every type's row looked like the same row.
      const newCostRow = { ...costRow, vehicleType: vehicle_type_row['vehicleType'], vehicleTypeId: vehicle_type_row['UUID'], UUID: generateUUID() };
      newState['data']['travel_costs'].push(JSON.parse(JSON.stringify(newCostRow)));
    });
  } else {
    newState['data']['travel_costs'].push(costRow);
  }
};

const addLocationCostRows = (newState, state, newRow) => {
  const newUuid = newRow['UUID'];
  const newLoc = newRow['locationName'];
  // Decided ONCE per added location, not once per pair: the decision reads the
  // existing rows, and these matrices reach 23k of them. Per pair it would be
  // quadratic in the table.
  const vehicleTypes = vehicleTypesForNewCostRows(state);
  state['data']['locations'].forEach((row) => {
    const iterUuid = row['UUID'];
    const iterLoc = row['locationName'];
    pushCostRowsForVehicleTypes(newState, {
      UUID: generateUUID(), fromLocationName: iterLoc, toLocationName: newLoc,
      duration: null, distance: null, cost: null,
      fromLocationId: iterUuid, toLocationId: newUuid,
      Valid: 'warning', validationMessage: COST_VALIDATION_EMPTY_WARNING,
    }, vehicleTypes);
    if (newLoc !== iterLoc) {
      pushCostRowsForVehicleTypes(newState, {
        UUID: generateUUID(), fromLocationName: newLoc, toLocationName: iterLoc,
        duration: null, distance: null, cost: null,
        fromLocationId: newUuid, toLocationId: iterUuid,
        Valid: 'warning', validationMessage: COST_VALIDATION_EMPTY_WARNING,
      }, vehicleTypes);
    }
  });
};

/*
A new vehicle type in a typed matrix needs its own rows, or nothing can drive it.

The solver builds a cost matrix per vehicle type and indexes it by every
location: a type whose rows don't exist isn't a type with missing costs, it's a
failed run (a pandas KeyError). Only `locations` used to add cost rows, so a
scenario that gained a vehicle type after its matrix was built was one Run away
from that.

One empty row per (pair, new type), skipping pairs that already have one — so
re-adding a type, or adding one to a matrix that anticipated it, changes
nothing. An untyped matrix is left alone: its rows already apply to every
vehicle, and typing rows for one type would be the mixing this whole change
exists to prevent.
*/
const addVehicleTypeCostRows = (newState, state, newRow) => {
  const existing = state['data']['travel_costs'];
  if (!Array.isArray(existing) || existing.length === 0) return;
  if (!travelCostsAreTyped(existing)) return;
  const newTypeId = newRow['UUID'];
  const newTypeName = newRow['vehicleType'];
  if (!newTypeId) return;

  // One pass: a representative row per pair, and the pairs this type already has.
  const representatives = new Map();
  const covered = new Set();
  for (const row of existing) {
    if (row == null || row.fromLocationId == null || row.toLocationId == null) continue;
    const key = `${row.fromLocationId}\u0000${row.toLocationId}`;
    if (!representatives.has(key)) representatives.set(key, row);
    if (row.vehicleTypeId === newTypeId) covered.add(key);
  }

  for (const [key, row] of representatives) {
    // Already has a row for this type: re-adding a type, or adding one to a
    // matrix that anticipated it, must not produce a second row for the same
    // (from, to, type). Duplicate pairs crash the solver exactly as missing
    // ones do.
    if (covered.has(key)) continue;
    /*
    A location to itself costs nothing, and saying so beats leaving a gap.
    An empty self-pair row is worse than none at all: the backend fills in
    missing self-pairs itself, but only MISSING ones — a row that exists and
    is empty is left empty, and the Run check doesn't count self-pairs, so
    nothing would ever have said so.
    */
    const self = row.fromLocationId === row.toLocationId;
    newState['data']['travel_costs'].push({
      UUID: generateUUID(),
      fromLocationName: row.fromLocationName, toLocationName: row.toLocationName,
      duration: self ? 0 : null, distance: self ? 0 : null, cost: self ? 0 : null,
      fromLocationId: row.fromLocationId, toLocationId: row.toLocationId,
      vehicleType: newTypeName, vehicleTypeId: newTypeId,
      Valid: self ? true : 'warning',
      validationMessage: self ? '' : COST_VALIDATION_EMPTY_WARNING,
    });
  }
};

export const addRowHelper = ({state, newRow, uuid, tableName, idx=null, validateCorrespondingColumns=true, addCostRow=true}) => {
  /**
   * Helper function that will add a row to a table. It will also handle any necessary updates to related tables.
   * Args:
   *  state: the current state of the store
   *  newRow: the new row to add to the table
   *  uuid: the uuid to assign to the new row
   *  tableName: the name of the table to add the row to
   *  idx: the index to add the row at. If null, the row will be added to the end of the table
   *  validateCorrespondingColumns: boolean, whether to validate corresponding columns in other tables
   *  addCostRow: boolean, whether to add a travel cost row if a new location is added
   */

  let newState = {...state};
  newRow['Valid'] = true;
  newRow['UUID'] = uuid;

  applyTableSpecificDefaults(newRow, state, tableName);
  const correspondingTables = applyColumnDefaultsAndValidation(newRow, state, tableName, validateCorrespondingColumns);
  applyDropdownKeys(newRow, state, tableName);

  if (idx === 0) {
    newState['data'][tableName].unshift(newRow);
  } else if (!idx) {
    newState['data'][tableName].push(newRow);
  } else {
    newState['data'][tableName].splice(idx, 0, newRow);
  }

  let rowToCheck = idx;
  if (!idx && idx !== 0) {
    rowToCheck = newState['data'][tableName].length - 1;
  }
  newState = validateRow(newState, tableName, rowToCheck);

  if (tableName === 'locations') {
    const newLoc = newRow['locationName'];
    const newLat = newRow['latitude'];
    const newLng = newRow['longitude'];
    newState['data']['trips'].forEach((row, rowIdx) => {
      if (row['fromLocationName'] === newLoc) {
        newState['data']['trips'][rowIdx]['From Latitude'] = newLat;
        newState['data']['trips'][rowIdx]['From Longitude'] = newLng;
      }
      if (row['toLocationName'] === newLoc) {
        newState['data']['trips'][rowIdx]['To Latitude'] = newLat;
        newState['data']['trips'][rowIdx]['To Longitude'] = newLng;
      }
    });
    if (addCostRow) {
      addLocationCostRows(newState, state, newRow);
    }
  }

  if (tableName === 'vehicle_types' && addCostRow) {
    addVehicleTypeCostRows(newState, state, newRow);
  }

  Array.from(correspondingTables).forEach((correspondingTableName) => {
    newState = validateTable(newState, correspondingTableName);
  });

  return newState;
}

const computeInsertIdx = (data, tableName, insertLoc, insertUuid, currentIdx) => {
  if (!insertLoc || !insertUuid) return currentIdx;
  const existanceCheck = existsInTable(data, tableName, ['UUID'], [insertUuid]);
  if (!existanceCheck || !existanceCheck.exists) return currentIdx;
  const rowLoc = existanceCheck.index;
  return insertLoc === 'above' ? rowLoc : rowLoc + 1;
};

const prepareTripsRow = (newRow, multiInsert, state) => {
  if (multiInsert) {
    newRow.forEach((newRow_, newRowIdx) => {
      newRow[newRowIdx] = checkTripUuids(newRow_, state['data']['locations']);
    });
    return newRow;
  }
  return checkTripUuids(newRow, state['data']['locations']);
};

export const addRowAction = (state, action) => {
    if (action.payload.clearResults) {
        clearResultsHelper(state, action.payload.resetMapLayers)
    }

    if (action.payload.clearExisting) {
        clearTableDataHelper(state, action.payload.tableName)
    }

    const activeResultsTable = STATIC_MODEL_SETTINGS[state.activeModelType]['resultsTable'];
    if (state.data[activeResultsTable] && state.data[activeResultsTable].length > 0) {
        state.notifications['warning'].push({
            'type': 'warning',
            'message': `Adding new data will clear results from your latest optimization run. Your previous runs are still in the "${SAVED_RUNS_PANEL}" panel in the sidebar.`,
            'payload': action.payload,
            'action': 'addRow',
        });
        return;
    }

    let newRow = JSON.parse(JSON.stringify(action.payload.newRow));
    const tableName = action.payload.tableName;
    const uuid = action.payload.uuid;
    let idx = computeInsertIdx(state['data'], tableName, action.payload.insertLoc, action.payload.insertUuid, action.payload.idx);
    const multiInsert = action.payload.multiInsert || false;
    const fromUpload = action.payload.fromUpload || false;

    if (!uuid) {
        throw new Error('uuid not received in add row');
    }

    if (tableName === 'trips') {
        newRow = prepareTripsRow(newRow, multiInsert, state);
    }

    if (multiInsert) {
        if (state.loginData.limits[tableName] && state.loginData.limits[tableName]['maxRows'] < state.data[tableName].length + newRow.length) {
            state.notifications['warning'].push({
                'type': 'warning',
                'message': 'Unable to add new rows, as total table size for the ' + tableName + ' table will exceed the maximum size allowed with your account (' + state.loginData.limits[tableName]['maxRows'] + ' rows).',
                'action': 'maxRowsExceeded',
            });
            return;
        }
        newRow.forEach((newRow_, newRowIdx) => {
            state['data'] = addRowHelper({state:state, newRow:newRow_, uuid:uuid[newRowIdx], tableName:tableName, idx: idx})['data'];
            state['geoJson'] = addRowGeoJsonHelper(state['geoJson'], uuid[newRowIdx], newRow_, tableName);
        });
    } else if (state.loginData.limits[tableName] && state.loginData.limits[tableName]['maxRows'] <= state.data[tableName].length) {
        state.notifications['warning'].push({
            'type': 'warning',
            'message': 'Unable to add new row, as total table size for the ' + tableName + ' table will exceed the maximum size allowed with your account (' + state.loginData.limits[tableName]['maxRows'] + ' rows).',
            'action': 'maxRowsExceeded',
        });
        return;
    } else {
        state['data'] = addRowHelper({state:state, newRow:newRow, uuid:uuid, tableName:tableName, idx: idx})['data'];
        state['geoJson'] = addRowGeoJsonHelper(state['geoJson'], uuid, newRow, tableName);
    }

    if (fromUpload) {
        state.tableSettings[tableName]['lastUpload'] = new Date().toLocaleString();
    }
}